import { Link } from "react-router-dom";

/**
 * PolicyPage — shared layout for About, Contact, Shipping & Returns,
 * Terms, Care Guide and other content pages.
 *
 * Props:
 *   eyebrow  — small uppercase label above title (e.g. "Sumanya · Legal")
 *   title    — main heading (H1)
 *   subtitle — one-line tagline under the title
 *   updated  — "Last updated" string (optional)
 *   sections — array of { heading, body?, bullets?, note? }
 *   footer   — optional React node rendered after all sections
 *   testId   — data-testid root (default "policy-page")
 */
export default function PolicyPage({
  eyebrow,
  title,
  subtitle,
  updated,
  sections = [],
  footer,
  testId = "policy-page",
}) {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid={testId}>
      {/* Hero */}
      <div className="mb-12">
        {eyebrow && (
          <p className="label-small text-[#C5A059] mb-3" data-testid="page-eyebrow">
            {eyebrow}
          </p>
        )}
        <h1
          className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2C2A29]"
          data-testid="page-title"
        >
          {title}
        </h1>
        {subtitle && (
          <p className="text-lg text-[#595552] mt-4 max-w-2xl leading-relaxed" data-testid="page-subtitle">
            {subtitle}
          </p>
        )}
        {updated && (
          <p className="text-xs text-[#8A8480] mt-4">Last updated: {updated}</p>
        )}
        <div className="mt-6 h-px w-24 bg-[#C5A059]" />
      </div>

      {/* Sections */}
      <div className="space-y-10">
        {sections.map((s, i) => (
          <section key={i} data-testid={`policy-section-${i}`}>
            {s.heading && (
              <h2 className="font-display text-2xl sm:text-3xl text-[#2C2A29] mb-4">
                {s.heading}
              </h2>
            )}
            {s.body && (
              Array.isArray(s.body) ? (
                s.body.map((para, j) => (
                  <p key={j} className="text-[15px] text-[#595552] leading-[1.75] mb-3">
                    {para}
                  </p>
                ))
              ) : (
                <p className="text-[15px] text-[#595552] leading-[1.75] mb-3">{s.body}</p>
              )
            )}
            {s.bullets && (
              <ul className="list-disc pl-6 space-y-1.5 text-[15px] text-[#595552] leading-[1.7] mt-2">
                {s.bullets.map((b, k) => (
                  <li key={k}>{b}</li>
                ))}
              </ul>
            )}
            {s.note && (
              <div className="mt-4 p-4 rounded-2xl bg-[#FFF5F3] border border-[#EAE3D9] text-sm text-[#595552]">
                {s.note}
              </div>
            )}
          </section>
        ))}
      </div>

      {footer && <div className="mt-16">{footer}</div>}

      {/* Common help footer */}
      <div className="mt-16 pt-10 border-t border-[#EAE3D9] flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <div className="font-display text-xl text-[#2C2A29] mb-1">Need help?</div>
          <p className="text-sm text-[#8A8480]">
            Phone: <a href="tel:+918210981248" className="text-[#8B1E28] hover:underline" data-testid="help-phone">+91-8210981248</a>
            {" · "}
            Email: <a href="mailto:info@sumanya.co" className="text-[#8B1E28] hover:underline" data-testid="help-email">info@sumanya.co</a>
          </p>
        </div>
        <Link
          to="/contact"
          className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-[#8B1E28] text-white text-sm hover:bg-[#7A1A24] transition"
          data-testid="cta-contact-us"
        >
          Contact Us
        </Link>
      </div>
    </div>
  );
}
